import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Checkbox, Alert } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import { CoaNode } from '../types';
import { CoaDTO, FxChartDTO } from '@/shared/api/model';
import { useGetAllCoas } from '@/shared/api/client/coa-resource';
import {getFxDSetting, useGetFxDSetting} from "@/shared/api/client/coa-resource";
import {useGetAllFxCharts} from "@/shared/api/client/fx-chart-resource";
import {getCsrfToken} from "@/shared/utils/utils";

const { Option } = Select;

interface CoaFormDialogProps {
    open: boolean;
    mode: 'view' | 'add' | 'edit' | 'delete';
    initialData?: Partial<CoaNode>;
    onCancel: () => void;
    onSubmit: (data: Partial<CoaDTO>) => Promise<void>;
}

export const CoaFormDialog: React.FC<CoaFormDialogProps> = ({
                                                                open,
                                                                mode,
                                                                initialData,
                                                                onCancel,
                                                                onSubmit,
                                                            }) => {
    const { dark } = useTheme();
    const [form] = Form.useForm();
    const [loading, setLoading] = React.useState(false);
    const [ignoreCheck, setIgnoreCheck] = React.useState(false);

    // Get currency data for dropdown
    const { data: fxCharts } = useGetAllFxCharts(
        //@ts-ignore
        {pageable: {page: 0, size: 900}},
        {
            query: {
                enabled: open,
            },
        }
    );

    useEffect(() => {
        if (open && initialData) {
            form.setFieldsValue(initialData);
        } else {
            form.resetFields();
        }
    }, [open, initialData, form]);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            // If it's a delete operation, just pass the initial data
            if (mode === 'delete') {
                await onSubmit(initialData as CoaDTO);
            } else {
                const submitData = {
                    ...initialData,
                    ...values,
                    ignoreCheck,
                };
                await onSubmit(submitData);
            }

            form.resetFields();
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const productTypes = [
        { value: ' ', label: 'General' },
        { value: 'S', label: 'Savings' },
        { value: 'H', label: 'Shares' },
        { value: 'F', label: 'Fixed Deposit' },
        { value: 'L', label: 'Loans' },
    ];

    const accountTypes = [
        'Assets',
        'Liabilities',
        'Equity',
        'Income',
        'Expenses',
    ];

    const isReadOnly = mode === 'view' || mode === 'delete';

    const modalTitle = {
        view: 'View Chart of Account',
        add: 'Add Chart of Account',
        edit: 'Edit Chart of Account',
        delete: 'Delete Chart of Account',
    }[mode];

    const modalProps = {
        view: { okButtonProps: { style: { display: 'none' } } },
        add: { okText: 'Add', okButtonProps: { type: 'primary' } },
        edit: { okText: 'Update', okButtonProps: { type: 'primary' } },
        delete: {
            okText: 'Delete',
            okButtonProps: { danger: true },
        },
    }[mode];

    return (
        // @ts-ignore
        <Modal
            title={modalTitle}
            open={open}
            onCancel={onCancel}
            onOk={handleSubmit}
            confirmLoading={loading}
            width={700}
            {...modalProps}
            className={dark ? 'dark' : ''}
        >
            {mode === 'delete' ? (
                <Alert
                    message="Confirm Deletion"
                    description="Are you sure you want to delete this chart of account? This action cannot be undone."
                    type="warning"
                    showIcon
                />
            ) : (
                <Form
                    form={form}
                    layout="vertical"
                    disabled={isReadOnly}
                >
                    <Form.Item
                        label="GL Number"
                        name="glNo"
                        rules={[{ required: true, message: 'Please input GL number' }]}
                    >
                        <Input
                            readOnly={mode === 'edit'}
                        />
                    </Form.Item>
                    <Form.Item name="_csrf" hidden initialValue={getCsrfToken()}>
                        <Input hidden/>
                    </Form.Item>

                    <Form.Item
                        label="Name"
                        name="acName"
                        rules={[{ required: true, message: 'Please input account name' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Account Type"
                        name="acType"
                        rules={[{ required: true, message: 'Please select account type' }]}
                    >
                        <Select>
                            {accountTypes.map(type => (
                                <Option key={type} value={type}>
                                    {type}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Currency Code"
                        name="fxCrncy"
                        rules={[{ required: true, message: 'Please select currency' }]}
                    >
                        <Select disabled={mode === 'edit'}>
                            {fxCharts?.map((chart: FxChartDTO) => (
                                <Option
                                    key={chart.fxCrncy}
                                    value={chart.fxCrncy}
                                    disabled={mode === 'edit' && chart.fxCrncy !== initialData?.fxCrncy}
                                >
                                    {`${chart.fxCrncy} | ${chart.title}`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Product Type"
                        name="product"
                        rules={[{ required: true, message: 'Please select product type' }]}
                    >
                        <Select disabled={mode === 'edit' && (initialData?.child || 0) > 0}>
                            {productTypes.map(type => (
                                <Option key={type.value} value={type.value}>
                                    {type.label}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {initialData && mode !== 'add' && (
                        <>
                            <Form.Item label="No. of Children" name="child">
                                <Input readOnly />
                            </Form.Item>

                            <Form.Item label="No. of Accounts" name="noOfAccounts">
                                <Input readOnly />
                            </Form.Item>
                        </>
                    )}

                    {mode === 'add' && (
                        <Form.Item>
                            <Checkbox
                                checked={ignoreCheck}
                                onChange={(e) => setIgnoreCheck(e.target.checked)}
                            >
                                Ignore Range Check
                            </Checkbox>
                        </Form.Item>
                    )}
                </Form>
            )}
        </Modal>
    );
};